import { debug } from '../utils/debug';
import { captureWarning } from '../utils/sentry';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { m, LazyMotion, domAnimation } from 'framer-motion';
import { FiCheck, FiAlertCircle, FiLoader, FiRefreshCw, FiClock } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { STRIPE_CONFIG } from '../config/stripe';
import { buildPath } from '../utils/routing';
import { logFallbackEvent } from '../utils/fallbackTelemetry';
import { useMotionConfig } from '../hooks/useReducedMotion';

/**
 * PaymentSuccess / PaymentReturn page
 *
 * Handles the return from Stripe 3DS authentication redirect.
 * This page:
 * 1. Extracts payment_intent from URL (Stripe adds it)
 * 2. Verifies payment status via BACKEND API (not trusting URL params)
 * 3. Creates booking if payment succeeded
 * 4. Shows retry option if payment failed
 *
 * Works with both:
 * - sessionStorage (same browser session)
 * - Database pending_bookings (for mobile 3DS where sessionStorage is lost)
 */
const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const motion = useMotionConfig();
  const [status, setStatus] = useState('loading'); // loading, success, error, requires_retry, refund_pending
  const [message, setMessage] = useState(t('paymentReturn.messages.verifying'));
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [merchantId, setMerchantId] = useState('rideshare'); // Default fallback
  const [errorDetails, setErrorDetails] = useState(null);
  // Async-settlement ('processing') state: whether the auto-poll has stopped,
  // whether a manual "Check status" request is in flight, and any inline note.
  const [pollExhausted, setPollExhausted] = useState(false);
  const [checking, setChecking] = useState(false);
  const [recheckNote, setRecheckNote] = useState(null);
  const pollTimerRef = useRef(null);
  // Fires the 'fallback_completed' beacon at most once per return.
  const fallbackCompletedLoggedRef = useRef(false);

  // Single status check shared by the auto-poll and the manual "Check status"
  // button. Applies the success/failure transitions and returns an outcome
  // ('settled' | 'failed' | 'processing') so callers can decide what to do next.
  const runStatusCheck = useCallback(async (piId) => {
    const apiUrl = STRIPE_CONFIG.apiBaseUrl || '/api';
    const resp = await fetch(`${apiUrl}/verify-payment-status`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentIntentId: piId })
    });
    if (!resp.ok) throw new Error('Failed to verify payment status');
    const result = await resp.json();

    if (result.existingBooking) {
      setBookingDetails({
        orderId: result.existingBooking.orderId,
        bookingId: result.existingBooking.id
      });
      setPaymentDetails({ paymentIntentId: piId, status: 'succeeded' });
      setMessage(t('paymentReturn.messages.confirmed'));
      setStatus('success'); // success + orderId → redirect effect forwards to confirmation
      return 'settled';
    }
    if (result.requiresRetry) {
      setErrorDetails(result.lastError);
      setMessage(t('paymentReturn.messages.couldNotComplete'));
      setStatus('requires_retry');
      return 'failed';
    }
    return 'processing';
  }, [t]);

  // Clear any pending poll timer when the page unmounts.
  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const handlePaymentReturn = async () => {
      debug.log('🎉 [PAYMENT-RETURN] Payment return page loaded');

      // Parse payment_intent from URL
      // Stripe appends params like: ?payment_intent=pi_xxx&redirect_status=xxx
      // For path-based route: /payment-return?payment_intent=xxx
      // For hash-based route: /?payment_intent=xxx#/payment-success
      const urlParams = new URLSearchParams(window.location.search);
      let paymentIntentId = searchParams.get('payment_intent') || urlParams.get('payment_intent');
      // Hosted-checkout fallback returns with ?session_id=cs_xxx (no PI in the
      // redirect). Resolve it to the PaymentIntent server-side, then continue
      // exactly as the 3DS flow does.
      const checkoutSessionId = searchParams.get('session_id') || urlParams.get('session_id');

      debug.log('🎉 [PAYMENT-RETURN] URL params:', {
        paymentIntentId,
        checkoutSessionId,
        fullUrl: window.location.href
      });

      if (!paymentIntentId && checkoutSessionId) {
        try {
          const apiUrl = STRIPE_CONFIG.apiBaseUrl || '/api';
          const resolveResp = await fetch(`${apiUrl}/verify-payment-status`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ checkoutSessionId })
          });
          if (resolveResp.ok) {
            const resolved = await resolveResp.json();
            paymentIntentId = resolved.paymentIntentId || null;
          }
        } catch (e) {
          console.error('Failed to resolve checkout session:', e);
        }
      }

      if (!paymentIntentId) {
        setStatus('error');
        setMessage(t('paymentReturn.messages.noPaymentInfo'));
        return;
      }

      // STEP 1: Verify payment status via BACKEND (source of truth)
      setMessage(t('paymentReturn.messages.verifyingWithBank'));

      try {
        const apiUrl = STRIPE_CONFIG.apiBaseUrl || '/api';
        const verifyResponse = await fetch(`${apiUrl}/verify-payment-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ paymentIntentId })
        });

        if (!verifyResponse.ok) {
          throw new Error('Failed to verify payment status');
        }

        const verifyResult = await verifyResponse.json();
        debug.log('🎉 [PAYMENT-RETURN] Verification result:', verifyResult);

        // Extract merchant ID for navigation
        if (verifyResult.pendingBooking?.merchantId) {
          setMerchantId(verifyResult.pendingBooking.merchantId);
        }

        // CASE 1: Booking already exists (created by webhook or previous attempt)
        if (verifyResult.existingBooking) {
          debug.log('✅ [PAYMENT-RETURN] Booking already exists:', verifyResult.existingBooking.orderId);
          setStatus('success');
          setMessage(t('paymentReturn.messages.confirmed'));
          setBookingDetails({
            orderId: verifyResult.existingBooking.orderId,
            bookingId: verifyResult.existingBooking.id
          });
          setPaymentDetails({
            paymentIntentId: paymentIntentId,
            status: verifyResult.paymentStatus
          });

          // Clean up sessionStorage
          sessionStorage.removeItem(`pendingBookingData-${paymentIntentId}`);
          return;
        }

        // CASE 2: Payment requires retry (failed authentication)
        if (verifyResult.requiresRetry) {
          debug.log('⚠️ [PAYMENT-RETURN] Payment requires retry:', verifyResult.paymentStatus);
          setStatus('requires_retry');
          setMessage(t('paymentReturn.messages.couldNotComplete'));
          setErrorDetails(verifyResult.lastError);
          setPaymentDetails({
            paymentIntentId: paymentIntentId,
            status: verifyResult.paymentStatus
          });
          return;
        }

        // CASE 2b: Payment is processing (async/redirect methods, e.g. Pay by
        // Bank). The customer is back from their bank but the payment hasn't
        // settled yet, so no booking exists. Show a positive "payment in
        // progress" state and poll for the booking the webhook will create once
        // Stripe reports 'succeeded'. If it settles while the customer is still
        // here, we forward them to the confirmation page.
        if (verifyResult.paymentProcessing) {
          debug.log('⏳ [PAYMENT-RETURN] Payment processing (async):', verifyResult.paymentStatus);
          setStatus('processing');
          setMessage(t('paymentReturn.messages.processing'));
          setPaymentDetails({
            paymentIntentId: paymentIntentId,
            status: verifyResult.paymentStatus
          });

          // Auto-poll with backoff for up to ~120s: every 3s for the first 30s
          // (catches the common fast settlement), then every 7s. Stops as soon
          // as the booking settles (→ confirmation) or hard-fails (→ retry). On
          // timeout we leave the customer on the positive "in progress" screen
          // with a manual "Check status" button — settlement can take far longer
          // than any poll, and the confirmation is emailed regardless (the
          // webhook is the source of truth, so nothing is lost if they leave).
          const startTime = Date.now();
          const MAX_MS = 120000;
          const scheduleNextPoll = () => {
            const delay = (Date.now() - startTime) < 30000 ? 3000 : 7000;
            pollTimerRef.current = setTimeout(async () => {
              let outcome = 'processing';
              try {
                outcome = await runStatusCheck(paymentIntentId);
              } catch (e) {
                console.error('Polling error:', e);
              }
              if (outcome !== 'processing') return; // already transitioned away
              if (Date.now() - startTime >= MAX_MS) {
                setPollExhausted(true);
                captureWarning(
                  '[payment] processing poll timed out without settlement',
                  { paymentIntentId },
                  { flow: 'payment' }
                );
                return;
              }
              scheduleNextPoll();
            }, delay);
          };
          scheduleNextPoll();

          return;
        }

        // CASE 3: Payment succeeded, create booking
        if (verifyResult.paymentSucceeded) {
          setMessage(t('paymentReturn.messages.verifiedCreating'));

          // Get booking data from database (preferred) or sessionStorage (fallback)
          let bookingData = verifyResult.pendingBooking?.bookingData;

          if (!bookingData) {
            // Fallback to sessionStorage
            const bookingDataKey = `pendingBookingData-${paymentIntentId}`;
            const savedBookingData = sessionStorage.getItem(bookingDataKey);
            if (savedBookingData) {
              bookingData = JSON.parse(savedBookingData);
              debug.log('📦 [PAYMENT-RETURN] Using booking data from sessionStorage');
            }
          } else {
            debug.log('📦 [PAYMENT-RETURN] Using booking data from database');
          }

          if (!bookingData) {
            // No booking data found anywhere - webhook should handle this
            debug.log('⚠️ [PAYMENT-RETURN] No booking data found, waiting for webhook...');
            setStatus('loading');
            setMessage(t('paymentReturn.messages.finalizing'));

            // Poll for booking creation (webhook might create it)
            let attempts = 0;
            const maxAttempts = 10;
            const pollInterval = setInterval(async () => {
              attempts++;
              try {
                const recheckResponse = await fetch(`${apiUrl}/verify-payment-status`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ paymentIntentId })
                });
                const recheckResult = await recheckResponse.json();

                if (recheckResult.existingBooking) {
                  clearInterval(pollInterval);
                  setStatus('success');
                  setMessage(t('paymentReturn.messages.confirmed'));
                  setBookingDetails({
                    orderId: recheckResult.existingBooking.orderId,
                    bookingId: recheckResult.existingBooking.id
                  });
                  setPaymentDetails({
                    paymentIntentId: paymentIntentId,
                    status: 'succeeded'
                  });
                }
              } catch (e) {
                console.error('Polling error:', e);
              }

              if (attempts >= maxAttempts) {
                clearInterval(pollInterval);
                setStatus('success');
                setMessage(t('paymentReturn.messages.confirmationShortly'));
                setPaymentDetails({
                  paymentIntentId: paymentIntentId,
                  status: 'succeeded'
                });
              }
            }, 2000);

            return;
          }

          // Extract merchant ID from booking data
          if (bookingData.merchantId) {
            setMerchantId(bookingData.merchantId);
          }

          // Create the booking
          try {
            const createResponse = await fetch(`${apiUrl}/create-booking`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(bookingData)
            });

            const result = await createResponse.json();
            debug.log('🎉 [PAYMENT-RETURN] Booking creation result:', result);

            if (result.success || result.idempotent) {
              setStatus('success');
              setMessage(t('paymentReturn.messages.confirmed'));
              setBookingDetails({
                orderId: result.booking?.orderId,
                bookingId: result.booking?.id
              });
              setPaymentDetails({
                paymentIntentId: paymentIntentId,
                status: 'succeeded'
              });

              // Clean up sessionStorage
              sessionStorage.removeItem(`pendingBookingData-${paymentIntentId}`);
            } else if (createResponse.status === 409 || result.code === 'INVENTORY_CONFLICT') {
              // Inventory was booked by someone else between save-pending-booking
              // and now. The webhook will independently refund + email the customer;
              // surface a clear refund-pending message instead of a generic error.
              setStatus('refund_pending');
              setMessage(t('paymentReturn.messages.inventoryConflictRefund'));
              setPaymentDetails({
                paymentIntentId: paymentIntentId,
                status: 'succeeded'
              });
              sessionStorage.removeItem(`pendingBookingData-${paymentIntentId}`);
            } else {
              throw new Error(result.error || 'Booking creation failed');
            }
          } catch (createError) {
            console.error('❌ [PAYMENT-RETURN] Booking creation error:', createError);
            setStatus('error');
            setMessage(t('paymentReturn.messages.bookingCreationFailed', {
              error: createError.message,
              paymentId: paymentIntentId
            }));
            setPaymentDetails({
              paymentIntentId: paymentIntentId,
              status: 'succeeded'
            });
          }
        } else {
          // Unexpected status
          setStatus('error');
          setMessage(t('paymentReturn.messages.unexpectedStatus', {
            status: verifyResult.paymentStatus
          }));
          setPaymentDetails({
            paymentIntentId: paymentIntentId,
            status: verifyResult.paymentStatus
          });
        }

      } catch (error) {
        console.error('❌ [PAYMENT-RETURN] Verification error:', error);
        setStatus('error');
        setMessage(t('paymentReturn.messages.unableToVerify', {
          paymentId: paymentIntentId
        }));
      }
    };

    handlePaymentReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Booking confirmed — clear the saved cart (sessionStorage + the localStorage
  // backup written by BookingFlow) so it can't resurface on a later visit.
  useEffect(() => {
    if (status === 'success') {
      try {
        sessionStorage.removeItem(`bookingData-${merchantId}`);
        localStorage.removeItem(`bookingDataBackup-${merchantId}`);
      } catch (e) { /* storage may be unavailable */ }
    }
  }, [status, merchantId]);

  // Redirect to unified confirmation page when booking is confirmed
  useEffect(() => {
    if (status === 'success' && bookingDetails?.orderId) {
      // A return carrying ?session_id= came from the secure-link fallback
      // (hosted Checkout). Record the completion so the funnel reads
      // shown → clicked → completed. sendBeacon survives the redirect below.
      try {
        const fromFallback = new URLSearchParams(window.location.search).get('session_id')
        if (fromFallback && !fallbackCompletedLoggedRef.current) {
          fallbackCompletedLoggedRef.current = true
          logFallbackEvent('fallback_completed', { merchantId, meta: { orderId: bookingDetails.orderId } })
        }
      } catch { /* telemetry is best-effort */ }

      // On a redirect return the booking is created by the webhook, so the raw
      // booking token never reached this browser. Exchange the PaymentIntent
      // client_secret (which Stripe appended to the return URL, and which only
      // the payer holds) for a fresh access token so the confirmation page can
      // be token-gated. Best-effort: if it fails, or the booking isn't ready yet
      // (202), we redirect without a token — the log-only phase tolerates that.
      ;(async () => {
        let issuedToken = ''
        try {
          const apiUrl = STRIPE_CONFIG.apiBaseUrl || '/api'
          const urlParams = new URLSearchParams(window.location.search)
          const paymentIntentId = paymentDetails?.paymentIntentId
          // 3DS/SCA returns payment_intent_client_secret; the hosted-checkout
          // fallback returns session_id. Use whichever this return carries as
          // proof to mint the access token.
          const clientSecret = searchParams.get('payment_intent_client_secret')
            || urlParams.get('payment_intent_client_secret')
          const checkoutSessionId = searchParams.get('session_id')
            || urlParams.get('session_id')

          let body = null
          if (paymentIntentId && clientSecret) body = { paymentIntentId, clientSecret }
          else if (checkoutSessionId) body = { checkoutSessionId }

          if (body) {
            const r = await fetch(`${apiUrl}/get-booking-token`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(body)
            })
            if (r.ok) issuedToken = (await r.json()).token || ''
          }
        } catch { /* non-fatal: confirmation still loads during log-only phase */ }

        const params = new URLSearchParams()
        params.set('pm', 'stripe_card')
        if (issuedToken) params.set('token', issuedToken)
        window.location.href = `/#${buildPath(`/confirmation/${bookingDetails.orderId}`, merchantId)}?${params.toString()}`
      })()
    }
  }, [status, bookingDetails, merchantId, paymentDetails])

  const handleReturnToBooking = () => {
    // PaymentSuccess runs inside BrowserRouter (App.jsx PaymentReturnHandler)
    // while the rest of the app uses HashRouter, so we switch routers with a
    // hard URL into the hash app. Use buildPath so the target is correct in
    // both subdomain mode (/#/locations) and path mode (/#/<merchant>/locations)
    // — hardcoding the /<merchant> prefix breaks routing on subdomain hosts.
    window.location.href = `/#${buildPath('/locations', merchantId)}`;
  };

  // Manual re-check from the processing screen. Re-runs the same status check
  // the auto-poll uses; on 'settled'/'failed' the screen transitions away, so
  // we only surface inline feedback when we're staying put.
  const handleCheckStatus = async () => {
    if (checking) return;
    const piId = paymentDetails?.paymentIntentId;
    if (!piId) return;
    setChecking(true);
    setRecheckNote(null);
    let outcome = 'error';
    try {
      outcome = await runStatusCheck(piId);
    } catch (e) {
      console.error('Manual status check error:', e);
    }
    if (outcome === 'processing') {
      setRecheckNote(t('paymentReturn.processingPending.stillProcessing'));
    } else if (outcome === 'error') {
      setRecheckNote(t('paymentReturn.processingPending.checkError'));
    }
    // Brief cooldown so the button can't be hammered against the endpoint.
    setTimeout(() => setChecking(false), 4000);
  };

  const handleRetryPayment = () => {
    // Hard reload to the checkout step so the React tree (and any cached
    // Stripe Element / PaymentIntent state) is fully torn down. The booking
    // data is in sessionStorage; Checkout.jsx will resume from it and
    // create a fresh PaymentIntent. navigate(-1) within BrowserRouter
    // doesn't reliably reset state and led to the failure box reappearing
    // even after a successful re-authorization. buildPath keeps the target
    // correct in both subdomain and path routing modes.
    window.location.href = `/#${buildPath('/checkout', merchantId)}`;
  };

  // Resolve the Stripe payment error into a localized, customer-friendly
  // message. Stripe's own `lastError.message` is always English, so we map
  // its `code` / `declineCode` to our own translated strings and fall back
  // to a generic localized message rather than surfacing Stripe's English.
  const getStripeErrorMessage = () => {
    if (!errorDetails) return t('paymentReturn.retry.fallbackError');

    const candidateKeys = [];
    if (errorDetails.declineCode) {
      candidateKeys.push(`paymentReturn.stripeErrors.${errorDetails.declineCode}`);
    }
    if (errorDetails.code === 'payment_intent_authentication_failure') {
      candidateKeys.push('paymentReturn.stripeErrors.authentication_failure');
    }
    if (errorDetails.code) {
      candidateKeys.push(`paymentReturn.stripeErrors.${errorDetails.code}`);
    }

    const MISSING = '__missing__';
    for (const key of candidateKeys) {
      const translated = t(key, { defaultValue: MISSING });
      if (translated !== MISSING) return translated;
    }

    return t('paymentReturn.stripeErrors.generic');
  };

  return (
    <LazyMotion features={domAnimation}>
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
      <m.div
        {...motion.scaleIn}
        className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center"
      >
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('paymentReturn.processing.title')}</h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}

        {/* Redirect to ConfirmationPage is imminent (see the effect that fires
            on success + orderId). Render a neutral spinner so the green
            "Booking Confirmed!" screen doesn't flash before ConfirmationPage
            loads. */}
        {status === 'success' && bookingDetails?.orderId && (
          <>
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiLoader} className="w-8 h-8 text-blue-600 animate-spin" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('paymentReturn.success.title')}</h1>
            <p className="text-gray-600">{t('paymentReturn.messages.confirmed')}</p>
          </>
        )}

        {/* Success without an orderId: the webhook hadn't created the booking
            in time, so there's no confirmation page to forward to. Show the
            full success details with the reassurance that the email will
            follow. */}
        {status === 'success' && !bookingDetails?.orderId && (
          <>
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiCheck} className="w-8 h-8 text-green-600" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('paymentReturn.success.title')}</h1>
            <p className="text-gray-600 mb-6">{message}</p>

            {bookingDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h2 className="font-semibold text-gray-900 mb-3">{t('paymentReturn.success.detailsHeading')}</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('paymentReturn.success.orderId')}:</span>
                    <span className="font-medium text-green-600">{bookingDetails.orderId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('paymentReturn.success.statusLabel')}:</span>
                    <span className="font-medium text-green-600">{t('paymentReturn.success.statusConfirmed')}</span>
                  </div>
                </div>
              </div>
            )}

            {paymentDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h2 className="font-semibold text-gray-900 mb-3">{t('paymentReturn.success.paymentHeading')}</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('paymentReturn.success.statusLabel')}:</span>
                    <span className="font-medium text-green-600">{t('paymentReturn.success.paymentStatusSuccessful')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('paymentReturn.success.paymentIdLabel')}:</span>
                    <span className="font-mono text-xs text-gray-900">
                      {paymentDetails.paymentIntentId?.substring(0, 20)}...
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {t('paymentReturn.success.emailSent')}
              </p>
              <button
                onClick={handleReturnToBooking}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                aria-label={t('paymentReturn.success.returnButton')}
              >
                {t('paymentReturn.success.returnButton')}
              </button>
            </div>
          </>
        )}

        {status === 'requires_retry' && (
          <>
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiRefreshCw} className="w-8 h-8 text-orange-600" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('paymentReturn.retry.title')}</h1>
            <p className="text-gray-600 mb-4">{message}</p>

            {errorDetails && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 text-left">
                <p className="text-sm text-orange-800">
                  {getStripeErrorMessage()}
                </p>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h2 className="font-semibold text-gray-900 mb-2">{t('paymentReturn.retry.whatHappened')}</h2>
              <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                <li>{t('paymentReturn.retry.reasonTimeout')}</li>
                <li>{t('paymentReturn.retry.reasonCancelled')}</li>
                <li>{t('paymentReturn.retry.reasonConnection')}</li>
              </ul>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                {t('paymentReturn.retry.bookingSaved')}
              </p>
              <button
                onClick={handleRetryPayment}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                aria-label={t('paymentReturn.retry.tryAgain')}
              >
                <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
                {t('paymentReturn.retry.tryAgain')}
              </button>
              <button
                onClick={handleReturnToBooking}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors"
                aria-label={t('paymentReturn.retry.startNew')}
              >
                {t('paymentReturn.retry.startNew')}
              </button>
            </div>
          </>
        )}

        {status === 'processing' && (
          <>
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
              <SafeIcon
                icon={pollExhausted ? FiClock : FiLoader}
                className={`w-8 h-8 text-blue-600 ${pollExhausted ? '' : 'animate-spin'}`}
                aria-hidden="true"
              />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('paymentReturn.processingPending.title')}</h1>
            <p className="text-gray-600 mb-4">
              {pollExhausted
                ? t('paymentReturn.processingPending.noteExhausted')
                : t('paymentReturn.messages.processing')}
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-800">{t('paymentReturn.processingPending.note')}</p>
            </div>

            {paymentDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h2 className="font-semibold text-gray-900 mb-3">{t('paymentReturn.processingPending.referenceHeading')}</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('paymentReturn.processingPending.paymentIdLabel')}:</span>
                    <span className="font-mono text-xs text-gray-900">
                      {paymentDetails.paymentIntentId?.substring(0, 20)}...
                    </span>
                  </div>
                </div>
              </div>
            )}

            {recheckNote && (
              <p className="text-sm text-gray-600 mb-4" role="status" aria-live="polite">{recheckNote}</p>
            )}

            <div className="space-y-3">
              <button
                onClick={handleCheckStatus}
                disabled={checking}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                aria-label={t('paymentReturn.processingPending.checkStatus')}
              >
                <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 ${checking ? 'animate-spin' : ''}`} />
                {checking
                  ? t('paymentReturn.processingPending.checking')
                  : t('paymentReturn.processingPending.checkStatus')}
              </button>
            </div>
          </>
        )}

        {status === 'refund_pending' && (
          <>
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-amber-600" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('paymentReturn.refund.title')}</h1>
            <p className="text-gray-600 mb-6">{message}</p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
              <h2 className="font-semibold text-gray-900 mb-2">{t('paymentReturn.refund.whatNext')}</h2>
              <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
                <li>{t('paymentReturn.refund.point1')}</li>
                <li>{t('paymentReturn.refund.point2')}</li>
                <li>{t('paymentReturn.refund.point3')}</li>
              </ul>
            </div>

            {paymentDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h2 className="font-semibold text-gray-900 mb-3">{t('paymentReturn.refund.referenceHeading')}</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('paymentReturn.refund.paymentIdLabel')}:</span>
                    <span className="font-mono text-xs text-gray-900">
                      {paymentDetails.paymentIntentId?.substring(0, 20)}...
                    </span>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handleReturnToBooking}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              aria-label={t('paymentReturn.refund.startNew')}
            >
              {t('paymentReturn.refund.startNew')}
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-600" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('paymentReturn.error.title')}</h1>
            <p className="text-gray-600 mb-6">{message}</p>

            {paymentDetails && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
                <h2 className="font-semibold text-gray-900 mb-3">{t('paymentReturn.error.referenceHeading')}</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('paymentReturn.error.paymentIdLabel')}:</span>
                    <span className="font-mono text-xs text-gray-900">
                      {paymentDetails.paymentIntentId?.substring(0, 20)}...
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={handleRetryPayment}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
                aria-label={t('paymentReturn.error.tryAgain')}
              >
                {t('paymentReturn.error.tryAgain')}
              </button>
              <button
                onClick={handleReturnToBooking}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-colors"
                aria-label={t('paymentReturn.error.returnButton')}
              >
                {t('paymentReturn.error.returnButton')}
              </button>
            </div>
          </>
        )}
      </m.div>
    </div>
    </LazyMotion>
  );
};

export default PaymentSuccess;
